/**
 * Bridge Strategy — 文件上传（MVP）
 *
 * 使用隐藏 <input type="file"> + 动态导入 CommonUploader。
 * 注意：上传需要用户已登录（fetchToken 需要认证），
 * section JS 应先调用 requireLogin。
 */
import type {
  BridgeUploadConfig,
  BridgeUploadResult,
  LandingBridgeMediator,
} from '../types';

const DEFAULT_ACCEPT = 'video/mp4,video/quicktime,image/png,image/jpeg,image/webp';
const DEFAULT_MAX_SIZE = 2 * 1024 * 1024 * 1024; // 2GB
const DEFAULT_SCENE_TYPE = 20;

export function registerUploadStrategy(bridge: LandingBridgeMediator): void {
  bridge.register(
    'openUpload',
    (config: BridgeUploadConfig, callback: (result: BridgeUploadResult) => void) => {
      const accept = config.accept || DEFAULT_ACCEPT;
      const maxSize = config.maxSize || DEFAULT_MAX_SIZE;
      const sceneType = config.sceneType || DEFAULT_SCENE_TYPE;

      // 创建隐藏 file input
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = accept;
      input.style.display = 'none';
      document.body.appendChild(input);

      // 用户取消文件选择时清理
      const handleWindowFocus = () => {
        setTimeout(() => {
          if (input.parentNode) document.body.removeChild(input);
        }, 300);
        window.removeEventListener('focus', handleWindowFocus);
      };
      window.addEventListener('focus', handleWindowFocus);

      input.onchange = async () => {
        window.removeEventListener('focus', handleWindowFocus);
        const file = input.files?.[0];
        if (input.parentNode) document.body.removeChild(input);

        if (!file) {
          callback({ success: false, error: 'No file selected' });
          return;
        }

        if (file.size > maxSize) {
          callback({
            success: false,
            error: `File too large (max ${Math.round(maxSize / 1024 / 1024)}MB)`,
          });
          return;
        }

        if (file.size === 0) {
          callback({ success: false, error: 'File is empty' });
          return;
        }

        try {
          const { Uploader } = await import('@/utils/Uploader/CommonUploader/index');
          const uploader = Uploader.getInstance();
          const url = await uploader.upload({
            file,
            tokenData: { fileName: file.name, dirSceneType: sceneType },
          });

          if (url) {
            callback({ success: true, url, fileName: file.name, fileSize: file.size });
          } else {
            callback({ success: false, error: 'Upload returned empty URL' });
          }
        } catch (err: any) {
          callback({ success: false, error: err.message || 'Upload failed' });
        }
      };

      input.click();
    },
  );
}
